import { useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Menu as MenuIcon, X } from 'lucide-react';
import { useAuth } from '@/app/providers/AuthProvider';
import type { PublicMenuItem } from '@/features/themes/lib/useSiteMenu';
import type { Block } from '@/types/blocks';
import type { AppearanceFields } from '@/features/themes/lib/appearance';
import { appearanceCssVars } from '../lib/brandingVars';
import { MenuNode, MenuLink } from './PublicMenuNav';
import { PublicBlockRenderer } from './PublicBlockRenderer';

interface PublicHeaderProps {
  siteSlug: string;
  siteName: string;
  logoUrl?: string | null;
  menu: PublicMenuItem[];
  siteId?: string;
  /** Quando definido (modelo de header ativado em Aparência), substitui totalmente o header padrão abaixo. */
  customBlocks?: Block[] | null;
  customAppearance?: AppearanceFields | null;
}

export function PublicHeader({ siteSlug, siteName, logoUrl, menu, siteId, customBlocks, customAppearance }: PublicHeaderProps) {
  const { session } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const hasCustomHeader = !!(customBlocks && customBlocks.length > 0);
  const customHeaderIsSticky = hasCustomHeader && customBlocks!.some((block) => block.sticky);

  useLayoutEffect(() => {
    const header = headerRef.current;
    const root = document.documentElement;
    if (!header || (hasCustomHeader && !customHeaderIsSticky)) return;

    const updateOffset = () => {
      const offset = header.getBoundingClientRect().height + 12;
      root.style.setProperty('--public-anchor-offset', `${Math.ceil(offset)}px`);
    };
    updateOffset();
    const observer = new ResizeObserver(updateOffset);
    observer.observe(header);
    return () => {
      observer.disconnect();
      root.style.removeProperty('--public-anchor-offset');
    };
  }, [hasCustomHeader, customHeaderIsSticky]);

  if (customBlocks && customBlocks.length > 0) {
    const hasStickyBlock = customBlocks.some((block) => block.sticky);
    const customStyle = customAppearance
      ? {
          ...appearanceCssVars(customAppearance),
          backgroundColor: 'var(--color-page-bg)',
          backgroundImage: 'var(--bg-image-page)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }
      : undefined;

    return (
      <header ref={headerRef} className={`w-full ${hasStickyBlock ? 'sticky top-0 z-50' : ''}`} style={customStyle}>
        <PublicBlockRenderer blocks={customBlocks} siteId={siteId} siteSlug={siteSlug} />
      </header>
    );
  }

  return (
    <header ref={headerRef} className="sticky top-0 z-50 border-b border-slate-900/5 bg-white/80 backdrop-blur-xl">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-6 py-4">
        <Link to={`/s/${siteSlug}`} className="flex items-center gap-2.5 shrink-0">
          {logoUrl ? (
            <img src={logoUrl} alt={siteName} className="h-8 w-auto" />
          ) : (
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: 'var(--color-accent)' }} />
          )}
          <span className="font-display text-lg font-bold tracking-tight text-slate-900">{siteName}</span>
        </Link>

        {/* Menu desktop */}
        {menu.length > 0 && (
          <nav className="hidden flex-1 items-center justify-center gap-1 md:flex">
            {menu.map((item) => (
              <MenuNode key={item.id} item={item} siteSlug={siteSlug} />
            ))}
          </nav>
        )}

        <div className="flex items-center gap-2 shrink-0">
          {session && (
            <a
              href="/app"
              className="hidden items-center gap-1.5 rounded-full px-5 py-2 text-sm font-semibold transition-all hover:-translate-y-0.5 md:inline-flex"
              style={{
                color: '#fff',
                background: 'linear-gradient(135deg, var(--color-primary), color-mix(in oklab, var(--color-primary), #000 18%))',
                boxShadow: '0 8px 24px -8px color-mix(in oklab, var(--color-accent), transparent 60%)',
              }}
            >
              Área do Admin <ArrowUpRight className="h-3.5 w-3.5" />
            </a>
          )}
          {menu.length > 0 && (
            <button
              onClick={() => setMobileOpen((v) => !v)}
              className="inline-flex items-center justify-center rounded-lg border border-slate-200 p-2 text-slate-700 md:hidden"
              aria-label="Abrir menu"
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <MenuIcon className="h-5 w-5" />}
            </button>
          )}
        </div>
      </div>

      {/* Menu mobile */}
      {mobileOpen && menu.length > 0 && (
        <nav className="border-t border-slate-900/5 bg-white px-6 py-4 md:hidden">
          <ul className="space-y-1">
            {menu.map((item) => (
              <li key={item.id}>
                <MenuLink item={item} siteSlug={siteSlug} className="block rounded-lg px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50" onNavigate={() => setMobileOpen(false)} />
                {item.children.length > 0 && (
                  <ul className="ml-3 border-l border-slate-100 pl-3">
                    {item.children.map((child) => (
                      <li key={child.id}>
                        <MenuLink item={child} siteSlug={siteSlug} className="block rounded-lg px-3 py-2 text-sm text-slate-500 hover:bg-slate-50" onNavigate={() => setMobileOpen(false)} />
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
          {session && (
            <a href="/app" className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-900">
              Área do Admin <ArrowUpRight className="h-3.5 w-3.5" />
            </a>
          )}
        </nav>
      )}
    </header>
  );
}
